import type { NodeLabel } from '@wt-codegraph/shared';

import {
  HIDDEN_BY_DEFAULT,
  NODE_COLORS,
  NODE_SIZES,
  getCommunityColor,
} from './graph-constants.js';

export interface SigmaNodeAttributes {
  x: number;
  y: number;
  size: number;
  color: string;
  label: string;
  kind: NodeLabel;
  filePath?: string;
  startLine?: number;
  hidden?: boolean;
  zIndex?: number;
  mass?: number;
  community?: number;
  communityColor?: string;
}

export interface SigmaEdgeAttributes {
  size: number;
  color: string;
  relationType: string;
  type?: string;
  curvature?: number;
  zIndex?: number;
}

const HIDDEN_KINDS = new Set<NodeLabel>(HIDDEN_BY_DEFAULT);
export const SYMBOL_KINDS = new Set<NodeLabel>([
  'Function',
  'Class',
  'Method',
  'Interface',
  'Struct',
]);

/** Lower minimum node size as graph density grows. */
export function scaleNodeSize(baseSize: number, nodeCount: number): number {
  if (nodeCount > 20_000) return Math.max(1.5, baseSize * 0.5);
  if (nodeCount > 5_000) return Math.max(2, baseSize * 0.65);
  if (nodeCount > 1_000) return Math.max(2.5, baseSize * 0.8);
  return baseSize;
}

/** Heavier mass = more repulsion in ForceAtlas2 → folders/modules push files apart. */
export function nodeMass(kind: NodeLabel, nodeCount: number): number {
  const baseMul = nodeCount > 5_000 ? 2 : nodeCount > 1_000 ? 1.5 : 1;
  switch (kind) {
    case 'Repo':
      return 50 * baseMul;
    case 'Module':
    case 'Namespace':
      return 20 * baseMul;
    case 'Directory':
      return 15 * baseMul;
    case 'File':
      return 3 * baseMul;
    case 'Class':
    case 'Interface':
    case 'Struct':
      return 5 * baseMul;
    case 'Function':
    case 'Method':
    case 'Constructor':
      return 2 * baseMul;
    default:
      return 1;
  }
}

export interface BuildNodeArgs {
  readonly id: string;
  readonly kind: NodeLabel;
  readonly name: string;
  readonly filePath?: string;
  readonly startLine?: number;
  readonly x: number;
  readonly y: number;
  readonly nodeCount: number;
  readonly community?: number;
}

export function buildNodeAttributes(args: BuildNodeArgs): SigmaNodeAttributes {
  const baseSize = NODE_SIZES[args.kind] ?? 4;
  const size = scaleNodeSize(baseSize, args.nodeCount);
  const usesCommunityColor = args.community !== undefined && SYMBOL_KINDS.has(args.kind);
  const color =
    usesCommunityColor && args.community !== undefined
      ? getCommunityColor(args.community)
      : (NODE_COLORS[args.kind] ?? '#9ca3af');
  return {
    x: args.x,
    y: args.y,
    size,
    color,
    label: args.name,
    kind: args.kind,
    ...(args.filePath ? { filePath: args.filePath } : {}),
    ...(args.startLine !== undefined ? { startLine: args.startLine } : {}),
    hidden: HIDDEN_KINDS.has(args.kind),
    mass: nodeMass(args.kind, args.nodeCount),
    ...(args.community !== undefined
      ? { community: args.community, communityColor: getCommunityColor(args.community) }
      : {}),
  };
}
